import { PageHeaderOptions } from '@/features/routes/types';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

type PageHeaderContextType = {
  pageHeaderOptions: PageHeaderOptions;
  setPageHeaderOptions: (options: PageHeaderOptions) => void;
  replacePageHeaderOptions: (options: PageHeaderOptions) => void;
};

const PageHeaderContext = createContext<PageHeaderContextType | undefined>(
  undefined,
);

export const PageHeaderProvider = ({
  children,
  initialValue,
}: {
  children: ReactNode;
  initialValue?: PageHeaderOptions;
}) => {
  const [pageHeaderOptions, setPageHeaderOptionsState] =
    useState<PageHeaderOptions>(initialValue || {});
  const setPageHeaderOptions = useCallback(
    (newOptions: Partial<PageHeaderOptions>) => {
      setPageHeaderOptionsState((prev) => ({
        ...prev,
        ...newOptions,
      }));
    },
    [],
  );

  const replacePageHeaderOptions = useCallback((options: PageHeaderOptions) => {
    setPageHeaderOptionsState(options);
  }, []);

  return (
    <PageHeaderContext.Provider
      value={{
        pageHeaderOptions,
        setPageHeaderOptions,
        replacePageHeaderOptions,
      }}
    >
      {children}
    </PageHeaderContext.Provider>
  );
};

export const usePageHeader = () => {
  const context = useContext(PageHeaderContext);
  if (!context) {
    throw new Error('usePageHeader must be used within PageHeaderProvider');
  }
  return context;
};
